import { apiClient } from '../client';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('MonitoringService');

interface ApiMetrics {
  requestCount: number;
  errorCount: number;
  averageResponseTime: number;
  successRate: number;
  cacheHitRate: number;
  endpoints: Record<string, {
    count: number;
    averageTime: number;
    errorCount: number;
  }>;
}

interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  responseTime: number;
  checks: {
    api: boolean;
    database?: boolean;
    cache?: boolean;
  };
}

interface PerformanceEntry {
  endpoint: string;
  method: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  status?: number;
}

export class MonitoringService {
  private metrics: ApiMetrics = {
    requestCount: 0,
    errorCount: 0,
    averageResponseTime: 0,
    successRate: 100,
    cacheHitRate: 0,
    endpoints: {},
  };

  private performanceEntries: PerformanceEntry[] = [];
  private readonly MAX_ENTRIES = 1000; // Keep last 1000 requests

  constructor() {
    this.setupInterceptors();
  }

  /**
   * Setup monitoring interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor to track start time
    apiClient.addRequestInterceptor((config, url) => {
      const entry: PerformanceEntry = {
        endpoint: url,
        method: config.method || 'GET',
        startTime: Date.now(),
        success: false,
      };

      // Store in performance entries
      this.performanceEntries.push(entry);

      // Keep only the latest entries
      if (this.performanceEntries.length > this.MAX_ENTRIES) {
        this.performanceEntries.shift();
      }

      return config;
    });

    // Response interceptor to track completion
    apiClient.addResponseInterceptor((response, url) => {
      const endTime = Date.now();
      
      // Find the corresponding entry
      const entry = this.performanceEntries
        .slice()
        .reverse()
        .find(e => e.endpoint === url && !e.endTime);

      if (entry) {
        entry.endTime = endTime;
        entry.duration = endTime - entry.startTime;
        entry.success = response.ok;
        entry.status = response.status;

        this.updateMetrics(entry);
      }

      return response;
    });

    // Error handler to track failed requests
    apiClient.addErrorHandler((error) => {
      this.metrics.errorCount++;
      this.updateSuccessRate();
      
      logger.debug('API Error tracked:', {
        errorCount: this.metrics.errorCount,
        successRate: this.metrics.successRate,
      });
    });
  }

  /**
   * Update metrics based on performance entry
   */
  private updateMetrics(entry: PerformanceEntry): void {
    this.metrics.requestCount++;

    // Update average response time
    const totalTime = this.metrics.averageResponseTime * (this.metrics.requestCount - 1) + (entry.duration || 0);
    this.metrics.averageResponseTime = totalTime / this.metrics.requestCount;

    // Update endpoint-specific metrics
    const endpointKey = `${entry.method} ${entry.endpoint}`;
    if (!this.metrics.endpoints[endpointKey]) {
      this.metrics.endpoints[endpointKey] = {
        count: 0,
        averageTime: 0,
        errorCount: 0,
      };
    }

    const endpointMetrics = this.metrics.endpoints[endpointKey];
    endpointMetrics.count++;

    if (entry.duration) {
      const endpointTotalTime = endpointMetrics.averageTime * (endpointMetrics.count - 1) + entry.duration;
      endpointMetrics.averageTime = endpointTotalTime / endpointMetrics.count;
    }

    if (!entry.success) {
      endpointMetrics.errorCount++;
    }

    this.updateSuccessRate();
  }

  /**
   * Update success rate
   */
  private updateSuccessRate(): void {
    if (this.metrics.requestCount > 0) {
      this.metrics.successRate = ((this.metrics.requestCount - this.metrics.errorCount) / this.metrics.requestCount) * 100;
    }
  }

  /**
   * Get current metrics
   */
  getMetrics(): ApiMetrics {
    return { ...this.metrics };
  }

  /**
   * Get performance data for specific endpoint
   */
  getEndpointPerformance(endpoint: string): PerformanceEntry[] {
    return this.performanceEntries.filter(entry => entry.endpoint.includes(endpoint));
  }

  /**
   * Reset metrics
   */
  resetMetrics(): void {
    this.metrics = {
      requestCount: 0,
      errorCount: 0,
      averageResponseTime: 0,
      successRate: 100,
      cacheHitRate: 0,
      endpoints: {},
    };
    this.performanceEntries = [];
  }

  /**
   * Perform health check
   */
  async performHealthCheck(): Promise<HealthCheck> {
    const startTime = Date.now();
    
    try {
      // Try to ping the API
      const response = await apiClient.get('/health', { 
        timeout: 5000,
        cache: false,
      });
      
      const responseTime = Date.now() - startTime;
      
      return {
        status: response.status === 'ok' ? 'healthy' : 'degraded',
        timestamp: new Date().toISOString(),
        responseTime,
        checks: {
          api: true,
          database: response.database,
          cache: response.cache,
        },
      };
    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      return {
        status: 'unhealthy',
        timestamp: new Date().toISOString(),
        responseTime,
        checks: {
          api: false,
        },
      };
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    slowestEndpoints: Array<{ endpoint: string; averageTime: number }>;
    errorProneEndpoints: Array<{ endpoint: string; errorRate: number }>;
    recentErrors: PerformanceEntry[];
  } {
    // Get slowest endpoints
    const slowestEndpoints = Object.entries(this.metrics.endpoints)
      .map(([endpoint, metrics]) => ({ endpoint, averageTime: metrics.averageTime }))
      .sort((a, b) => b.averageTime - a.averageTime)
      .slice(0, 5);

    // Get error-prone endpoints
    const errorProneEndpoints = Object.entries(this.metrics.endpoints)
      .map(([endpoint, metrics]) => ({
        endpoint,
        errorRate: (metrics.errorCount / metrics.count) * 100,
      }))
      .filter(item => item.errorRate > 0)
      .sort((a, b) => b.errorRate - a.errorRate)
      .slice(0, 5);

    // Get recent errors
    const recentErrors = this.performanceEntries
      .filter(entry => !entry.success)
      .slice(-10);

    return {
      slowestEndpoints,
      errorProneEndpoints,
      recentErrors,
    };
  }

  /**
   * Export metrics for external monitoring tools
   */
  exportMetrics(): string {
    const metrics = this.getMetrics();
    const summary = this.getPerformanceSummary();
    
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      metrics,
      summary,
    }, null, 2);
  }
}

// Export singleton instance
export const monitoringService = new MonitoringService(); 